import { useCallback, useRef, useState } from 'react';
import { AxiosResponse } from 'axios';
import { DirectionOption } from 'src/commons';
import { Publication } from 'src/app/models/business/Publication';
import { AxiosRequest } from 'src/lib/axios/Axios';
import { PublicationFilters } from '../components/PublicationsHeader/types';

export const PUBLICATIONS_PAGE_SIZE = 40;

const getFilterQueryValue = (
  key: keyof PublicationFilters,
  value: PublicationFilters[keyof PublicationFilters],
) => {
  if (key === 'marketplace') {
    return `'${encodeURIComponent(String(value))}'`;
  }

  if (key === 'zoneIds' && Array.isArray(value)) {
    return encodeURIComponent(`[${value.map((zoneId) => `'${zoneId}'`).join(',')}]`);
  }

  return encodeURIComponent(String(value));
};

type PaginatedPublicationsBody = {
  data?: Publication[];
  items?: Publication[];
  publications?: Publication[];
  total?: number;
  count?: number;
  totalCount?: number;
};

const parsePublicationsResponse = (
  response: AxiosResponse | { data?: unknown },
  pageNum: number,
): { items: Publication[]; total: number } => {
  const axiosResponse = response as AxiosResponse;
  const body = axiosResponse?.data;

  let items: Publication[] = [];
  if (Array.isArray(body)) {
    items = body;
  } else if (body && typeof body === 'object') {
    const paginated = body as PaginatedPublicationsBody;
    items = paginated.data ?? paginated.items ?? paginated.publications ?? [];
  }

  const headers = axiosResponse?.headers ?? {};
  const headerTotal =
    headers['x-total-count'] ??
    headers['X-Total-Count'] ??
    headers['total-count'] ??
    headers['Total-Count'];

  let total: number | undefined;
  if (headerTotal != null && headerTotal !== '') {
    total = Number(headerTotal);
  } else if (body && typeof body === 'object' && !Array.isArray(body)) {
    const paginated = body as PaginatedPublicationsBody;
    total = paginated.total ?? paginated.count ?? paginated.totalCount;
  }

  if (total == null || Number.isNaN(total)) {
    total =
      items.length < PUBLICATIONS_PAGE_SIZE
        ? (pageNum - 1) * PUBLICATIONS_PAGE_SIZE + items.length
        : pageNum * PUBLICATIONS_PAGE_SIZE + 1;
  }

  return { items, total };
};

const buildFilterQuery = (
  filter: PublicationFilters,
  orderBy?: { orderBy: string; direction: DirectionOption } | null,
) => {
  const query: string[] = [];
  const keys = Object.keys(filter) as Array<keyof PublicationFilters>;

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const value = filter[key];

    if (Array.isArray(value)) {
      if (value.length) {
        query.push(`${key}=${getFilterQueryValue(key, value)}`);
      }
    } else if (value) {
      query.push(`${key}=${getFilterQueryValue(key, value)}`);
    }
  }

  if (orderBy?.orderBy) {
    query.push(`orderBy=${orderBy.orderBy}`);
    query.push(`direction=${orderBy.direction}`);
  }

  return query.join('&');
};

export const usePublicationsData = () => {
  const [publications, setPublications] = useState<Publication[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const currentFilter = useRef<PublicationFilters | null>(null);
  const currentOrderBy = useRef<{ orderBy: string; direction: DirectionOption } | null>(null);
  const currentPageRef = useRef(1);

  const fetchPublications = useCallback(async (pageNum: number) => {
    setLoading(true);
    currentPageRef.current = pageNum;

    try {
      const filterQuery = buildFilterQuery(
        currentFilter.current ?? ({} as PublicationFilters),
        currentOrderBy.current,
      );
      const queryString = filterQuery
        ? `page=${pageNum}&${filterQuery}`
        : `page=${pageNum}`;
      const response = await AxiosRequest.get<Publication[] | PaginatedPublicationsBody>(
        `/publications?${queryString}`,
      );
      const { items, total } = parsePublicationsResponse(response, pageNum);
      setPublications(items);
      setTotalCount(total);
      setPage(pageNum - 1);
    } catch {
      setPublications([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const onPageChange = useCallback(
    (newPage: number) => {
      fetchPublications(newPage + 1);
    },
    [fetchPublications],
  );

  const reLoad = useCallback(() => {
    fetchPublications(currentPageRef.current);
  }, [fetchPublications]);

  const onFilter = useCallback(
    async (filter: PublicationFilters) => {
      const filterChanged = JSON.stringify(currentFilter.current) !== JSON.stringify(filter);
      if (!filterChanged) return;
      currentFilter.current = filter;
      await fetchPublications(1);
    },
    [fetchPublications],
  );

  const onOrderByChange = useCallback(
    async (_orderBy: string, direction: DirectionOption) => {
      const orderByChanged =
        JSON.stringify(currentOrderBy.current) !== JSON.stringify({ orderBy: _orderBy, direction });
      if (!orderByChanged) return;

      currentOrderBy.current = { orderBy: _orderBy, direction };
      await fetchPublications(1);
    },
    [fetchPublications],
  );

  return {
    data: publications,
    loading,
    totalCount,
    page,
    rowsPerPage: PUBLICATIONS_PAGE_SIZE,
    onPageChange,
    onFilter,
    reLoad,
    onOrderByChange,
  };
};
